/**
 * Site Profile — the item-level view from the FCCERS-3 Profile sheet (p. 97).
 *
 * Pick a site, pick which of its observations to overlay, and read the 33
 * items against each other. Where the rest of the dashboard answers "how is
 * this site doing", this answers "which items moved, and which did not".
 */

import { useMemo, useState, useEffect } from 'react';
import { LineChart, TrendingDown, TrendingUp } from 'lucide-react';
import { useDashboardContext } from '@/components/layout/DashboardShell';
import {
  SiteProfileChart,
  SERIES_COLORS,
} from '@/components/dashboard/SiteProfileChart';
import {
  buildSiteProfiles,
  hasItemDetail,
  itemDeltas,
} from '@/lib/dashboard/siteProfile';

export function SiteProfilePage() {
  const { all, filters } = useDashboardContext();
  // Deliberately built from every observation, not the filtered set: this page
  // exists to compare a site's visits over time, and the dashboard's date
  // range defaults to the last six months — which would silently hide the
  // earlier visit and leave nothing to compare against.
  const profiles = useMemo(() => buildSiteProfiles(all), [all]);
  const dateFilterActive = Boolean(filters.from || filters.to);

  const [siteName, setSiteName] = useState<string>('');
  const [picked, setPicked] = useState<string[]>([]);

  const site = profiles.find((p) => p.siteName === siteName) ?? profiles[0];

  // Default to the site with the most visits, and to its two most recent —
  // the comparison the sheet is designed for.
  useEffect(() => {
    if (!site) return;
    if (siteName !== site.siteName) setSiteName(site.siteName);
    setPicked(site.observations.slice(0, 2).map((o) => o.recordId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.siteName]);

  const shown = useMemo(
    () => (site ? site.observations.filter((o) => picked.includes(o.recordId)) : []),
    [site, picked],
  );

  const withoutDetail = all.length - all.filter(hasItemDetail).length;

  if (profiles.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <LineChart className="mx-auto h-8 w-8 text-muted-foreground" />
        <h2 className="mt-3 font-serif text-xl font-semibold text-dece-navy">
          No item-level data yet
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The profile is built from per-item scores, which are recorded with
          each submission. {all.length > 0
            ? `None of the ${all.length} observations currently in range carry them — they may pre-date that field, or the filters may be excluding them.`
            : 'Once observations are submitted they will appear here.'}
        </p>
      </div>
    );
  }

  const toggle = (id: string) =>
    setPicked((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-5),
    );

  const deltas =
    shown.length === 2 ? itemDeltas(shown[1], shown[0]).slice(0, 6) : [];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-6 py-6">
      <header>
        <h2 className="font-serif text-2xl font-bold text-dece-navy">
          Site profile
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every item scored, laid out as in the FCCERS-3 Profile sheet. Overlay
          visits to see which items changed.
          {dateFilterActive
            ? ' This page looks across all dates, so the date range above does not hide earlier visits.'
            : ''}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-4 rounded-xl border bg-card p-4 shadow-sm">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Site
          </span>
          <select
            value={site?.siteName ?? ''}
            onChange={(e) => setSiteName(e.target.value)}
            className="min-w-[260px] rounded-md border bg-background px-3 py-2 text-sm"
          >
            {profiles.map((p) => (
              <option key={p.siteName} value={p.siteName}>
                {p.siteName} ({p.observations.length})
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Observations to overlay
          </span>
          <div className="flex flex-wrap gap-2">
            {site?.observations.map((o) => {
              const idx = picked.indexOf(o.recordId);
              const on = idx >= 0;
              return (
                <button
                  key={o.recordId}
                  onClick={() => toggle(o.recordId)}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                    on
                      ? 'border-transparent text-white'
                      : 'bg-background text-muted-foreground hover:bg-accent'
                  }`}
                  style={
                    on
                      ? { backgroundColor: SERIES_COLORS[idx % SERIES_COLORS.length] }
                      : undefined
                  }
                >
                  <span className="font-medium">{o.date || 'undated'}</span>
                  <span className="opacity-80">
                    {o.totalAverage != null ? o.totalAverage.toFixed(2) : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {site && site.observations.length === 1 ? (
        <p className="rounded-md border border-amber-300/50 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
          This site has one observation, so there is nothing to compare against
          yet. The profile still shows where each item stands today.
        </p>
      ) : null}

      {shown.length === 0 ? (
        <p className="rounded-md border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Select at least one observation above.
        </p>
      ) : (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <SiteProfileChart items={site!.items} observations={shown} />
          <p className="mt-2 text-xs text-muted-foreground">
            Hollow markers to the left of an item mean it was scored Not
            Applicable for that visit.
          </p>
        </div>
      )}

      {deltas.length > 0 ? (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <h3 className="font-serif text-base font-semibold text-dece-navy">
            Biggest movements
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {shown[1].date} → {shown[0].date}
          </p>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {deltas.map((d) => {
              const item = site!.items.find((i) => i.number === d.number);
              const up = d.delta > 0;
              return (
                <li
                  key={d.number}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  {up ? (
                    <TrendingUp className="h-4 w-4 flex-shrink-0 text-rating-5" />
                  ) : (
                    <TrendingDown className="h-4 w-4 flex-shrink-0 text-destructive" />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {d.number}. {item?.name ?? ''}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {d.from} → {d.to}
                  </span>
                  <span
                    className={`tabular-nums font-semibold ${up ? 'text-rating-5' : 'text-destructive'}`}
                  >
                    {up ? '+' : ''}
                    {d.delta}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {withoutDetail > 0 ? (
        <p className="text-xs text-muted-foreground">
          {withoutDetail} observation{withoutDetail === 1 ? '' : 's'} in the
          current range {withoutDetail === 1 ? 'has' : 'have'} no item-level
          detail and {withoutDetail === 1 ? 'is' : 'are'} not shown here.
        </p>
      ) : null}
    </div>
  );
}
